import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import "./style.css";

const API = process.env.REACT_APP_API_BASE;
const ADMIN_EMAILS = [
  process.env.REACT_APP_ADMIN,
  process.env.REACT_APP_ARI_ADMIN,
].filter(Boolean);

/* Default content — used as fallback before the saved values load (or if none
   have ever been saved). Admins can override every field via the edit panel. */
const DEFAULT_CAMPAIGN = {
  title: "The Inner Oradea",
  subtitle: "Experiență senzorială",
  location: "Filarmonica de Stat, Oradea",
  date: "19-20 Iunie 2026",
  time: "9:00 - 14:00",
  link: "/campanie/the-inner-oradea",
  buttonLabel: "Rezervă Acum",
};

const FIELDS = [
  { key: "title",       label: "Titlu" },
  { key: "subtitle",    label: "Subtitlu" },
  { key: "location",    label: "Locație" },
  { key: "date",        label: "Dată" },
  { key: "time",        label: "Interval orar" },
  { key: "link",        label: "Link buton (ex: /eveniment/the-inner-oradea)" },
  { key: "buttonLabel", label: "Text buton" },
];

/* ── Admin edit modal ── */
const CampaignEditModal = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(() => ({ ...DEFAULT_CAMPAIGN, ...initial }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    if (!form.title || !form.title.trim()) {
      toast.error("Titlul este obligatoriu");
      return;
    }
    setSaving(true);
    try {
      const config = process.env.REACT_APP_TOKEN
        ? { headers: { Authorization: `Basic ${process.env.REACT_APP_TOKEN}` } }
        : {};
      await axios.put(`${API}/homeEvent/save`, form, config);
      toast.success("Conținutul a fost salvat!");
      if (onSaved) onSaved(form);
      onClose();
    } catch (err) {
      toast.error("Eroare la salvarea conținutului");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="campaign-edit-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className="campaign-edit-modal">
        <div className="campaign-edit-modal__header">
          <h3>Editează cardul</h3>
          <button className="campaign-edit-modal__close" onClick={onClose} aria-label="Închide">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="campaign-edit-modal__body">
          {FIELDS.map((f) => (
            <div className="campaign-edit-modal__field" key={f.key}>
              <label>{f.label}</label>
              <input
                type="text"
                value={form[f.key] || ""}
                onChange={(e) => setField(f.key, e.target.value)}
              />
            </div>
          ))}
        </div>

        <div className="campaign-edit-modal__footer">
          <button className="campaign-edit-modal__btn-cancel" onClick={onClose}>
            Anulează
          </button>
          <button
            className="campaign-edit-modal__btn-save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Se salvează..." : "Salvează"}
          </button>
        </div>
      </div>
    </div>
  );
};

const NewHomeEvent = () => {
  const email = useSelector((s) => s?.user?.user?.email);
  const isAdmin = !!email && ADMIN_EMAILS.includes(email);

  const [campaign, setCampaign] = useState(DEFAULT_CAMPAIGN);
  const [editing, setEditing] = useState(false);

  const fetchCampaign = useCallback(() => {
    axios
      .get(`${API}/homeEvent/get`)
      .then((res) => {
        const data = res.data || {};
        // Merge saved fields over defaults so any missing field falls back.
        const merged = { ...DEFAULT_CAMPAIGN };
        for (const { key } of FIELDS) {
          if (typeof data[key] === "string" && data[key]) merged[key] = data[key];
        }
        setCampaign(merged);
      })
      .catch(() => {});
  }, []);

  useEffect(() => { fetchCampaign(); }, [fetchCampaign]);

  return (
    <section className="new-campaign">
      <div className="new-campaign__inner">
        <div className="new-campaign__card new-campaign__card--featured">
          {isAdmin && (
            <button
              type="button"
              className="new-campaign__edit-btn"
              onClick={() => setEditing(true)}
              aria-label="Editează cardul"
              title="Editează cardul"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
              </svg>
            </button>
          )}

          <h3 className="new-campaign__title">{campaign.title}</h3>

          <div className="new-campaign__details">
            <div className="new-campaign__detail">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <span>{campaign.location}</span>
            </div>
            <div className="new-campaign__detail">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <span>{campaign.date}</span>
            </div>
            <div className="new-campaign__detail">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span>{campaign.time}</span>
            </div>
          </div>

          <Link to={campaign.link} className="new-campaign__btn">
            {campaign.buttonLabel || "Rezervă Acum"}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>

      {editing && (
        <CampaignEditModal
          initial={campaign}
          onClose={() => setEditing(false)}
          onSaved={(updated) => setCampaign(updated)}
        />
      )}
    </section>
  );
};

export default NewHomeEvent;
